import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DownloadRounded } from '@mui/icons-material';

const isStandaloneDisplay = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

const getInstallHelp = () => {
  const ua = window.navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isChrome = /Chrome|CriOS/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|EdgiOS/i.test(ua);

  if (isIOS && isSafari) {
    return {
      title: 'Install on iPhone or iPad',
      steps: [
        'Tap the Share button in Safari.',
        'Choose "Add to Home Screen".',
        'Tap "Add" to install Skhaftin.',
      ],
    };
  }

  if (isAndroid && isChrome) {
    return {
      title: 'Install on Android',
      steps: [
        'Open the browser menu.',
        'Tap "Install app" or "Add to Home screen".',
        'Confirm the install prompt.',
      ],
    };
  }

  return {
    title: 'Install this app',
    steps: [
      'Open your browser menu.',
      'Look for "Install app" or "Add to Home screen".',
      'Confirm to save Skhaftin on your device.',
    ],
  };
};

const PwaInstallButton = ({ fullWidth = false, variant = 'outlined', color = 'inherit', label = 'Download App' }) => {
  const [installPrompt, setInstallPrompt] = React.useState(null);
  const [installed, setInstalled] = React.useState(
    typeof window !== 'undefined' ? isStandaloneDisplay() : false
  );
  const [helpOpen, setHelpOpen] = React.useState(false);

  React.useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    const handleInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
      setHelpOpen(false);
    };

    const handleDisplayModeChange = () => {
      setInstalled(isStandaloneDisplay());
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    window.matchMedia?.('(display-mode: standalone)').addEventListener?.('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
      window.matchMedia?.('(display-mode: standalone)').removeEventListener?.('change', handleDisplayModeChange);
    };
  }, []);

  const installHelp = getInstallHelp();

  const handleInstall = async () => {
    if (installed) return;

    if (!installPrompt) {
      setHelpOpen(true);
      return;
    }

    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice?.outcome !== 'accepted') {
      setHelpOpen(true);
    }
    setInstallPrompt(null);
  };

  return (
    <>
      <Button
        variant={installed ? 'contained' : variant}
        color={installed ? 'success' : color}
        fullWidth={fullWidth}
        startIcon={<DownloadRounded />}
        onClick={handleInstall}
      >
        {installed ? 'App Installed' : label}
      </Button>
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{installHelp.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25}>
            {installHelp.steps.map((step) => (
              <Typography key={step} variant="body2">
                {step}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PwaInstallButton;
